import {
	CodeBlock,
	Alert,
	Badge,
	Table,
	Card,
	CardGrid,
} from '../../components/docs';
import styles from '../../components/docs/docs.module.css';

export default function Authentication() {
	const keyFormatsData = [
		{
			prefix: 'pk_test_',
			type: 'Public Key',
			environment: <Badge variant="sandbox">Sandbox</Badge>,
			usage: 'Client-side identification, game URL generation',
		},
		{
			prefix: 'sk_test_',
			type: 'Secret Key',
			environment: <Badge variant="sandbox">Sandbox</Badge>,
			usage: 'Server-side API calls, session creation',
		},
		{
			prefix: 'pk_live_',
			type: 'Public Key',
			environment: <Badge variant="live">Live</Badge>,
			usage: 'Client-side identification, game URL generation',
		},
		{
			prefix: 'sk_live_',
			type: 'Secret Key',
			environment: <Badge variant="live">Live</Badge>,
			usage: 'Server-side API calls, session creation',
		},
	];

	const keyFormatsColumns = [
		{
			key: 'prefix',
			header: 'Prefix',
			render: (value: unknown) => <code>{value as string}</code>,
		},
		{ key: 'type', header: 'Type' },
		{ key: 'environment', header: 'Environment' },
		{ key: 'usage', header: 'Usage' },
	];

	return (
		<div className={styles.docsPage}>
			<h1>Authentication</h1>
			<p className={styles.lead}>
				Ringfort uses API keys to authenticate requests. Your API keys carry
				significant privileges, so be sure to keep them secure. Don't share your
				secret keys in publicly accessible areas such as GitHub, client-side
				code, or browser storage.
			</p>

			<h2>API Key Formats</h2>

			<p>
				All API keys follow a consistent format that identifies their type and
				environment at a glance. Keys are prefixed to prevent accidental misuse
				between environments.
			</p>

			<Table columns={keyFormatsColumns} data={keyFormatsData} />

			<Alert variant="warning" title="Keep Your Secret Keys Secret">
				Secret keys (<code>sk_*</code>) should only be used in server-side code.
				Never expose them in client-side JavaScript, mobile apps, or public
				repositories. If you believe a secret key has been compromised, rotate
				it immediately from your operator dashboard.
			</Alert>

			<h2>Using Bearer Tokens</h2>

			<p>
				Authenticate API requests by including your secret key in the{' '}
				<code>Authorization</code> header using the Bearer scheme.
			</p>

			<CodeBlock language="bash" title="Authentication header format">
				{`Authorization: Bearer sk_test_your_secret_key_here`}
			</CodeBlock>

			<h3>Operator API Authentication</h3>

			<p>
				When making requests to the Operator API (creating sessions, managing
				players, etc.), use your secret key:
			</p>

			<CodeBlock language="bash" title="Example: Create a session">
				{`curl -X POST https://api.rngfort.com/api/v1/operators/sessions \\
  -H "Authorization: Bearer sk_test_abc123xyz789" \\
  -H "Content-Type: application/json" \\
  -d '{
    "playerId": "player_123",
    "currency": "USD",
    "walletMode": "integrated"
  }'`}
			</CodeBlock>

			<h3>Game API Authentication</h3>

			<p>
				When making requests to the Game API (placing bets, checking balance,
				etc.), use the session token returned from session creation:
			</p>

			<CodeBlock language="bash" title="Example: Place a bet">
				{`curl -X POST https://api.rngfort.com/api/v1/game/dice/roll \\
  -H "Authorization: Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "amount": "1000",
    "target": 50.00,
    "condition": "under"
  }'`}
			</CodeBlock>

			<Alert variant="info" title="Session Tokens vs API Keys">
				<p>
					<strong>API Keys</strong> (<code>sk_*</code>) are used to authenticate
					your server to Ringfort. They identify your operator account and are
					used to create player sessions.
				</p>
				<p>
					<strong>Session Tokens</strong> (JWT format) are used to authenticate
					individual player sessions. They are returned when you create a
					session and should be used for all game-related API calls.
				</p>
			</Alert>

			<h2>Environment-Specific Keys</h2>

			<p>
				Ringfort provides separate API keys for sandbox and live environments.
				Each environment is completely isolated - sandbox keys cannot access
				live data and vice versa.
			</p>

			<div
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(2, 1fr)',
					gap: 'var(--space-4)',
					margin: 'var(--space-5) 0',
				}}
			>
				<div className={`${styles.envCard} ${styles.sandbox}`}>
					<div className={styles.envCardHeader}>
						<h3 className={styles.envCardTitle}>Sandbox Keys</h3>
						<Badge variant="sandbox">Testing</Badge>
					</div>
					<p className={styles.envCardDescription}>
						Use during development and testing. Transactions are simulated and
						balances are virtual.
					</p>
					<ul>
						<li>Unlimited virtual balance</li>
						<li>No real money involved</li>
						<li>Test all API endpoints</li>
						<li>Debug with verbose logging</li>
					</ul>
				</div>

				<div className={`${styles.envCard} ${styles.live}`}>
					<div className={styles.envCardHeader}>
						<h3 className={styles.envCardTitle}>Live Keys</h3>
						<Badge variant="live">Production</Badge>
					</div>
					<p className={styles.envCardDescription}>
						Use in production. All transactions are real and balances affect
						actual player funds.
					</p>
					<ul>
						<li>Real wallet integration</li>
						<li>Actual transaction processing</li>
						<li>Full production security</li>
						<li>SLA guarantees apply</li>
					</ul>
				</div>
			</div>

			<h2>Security Best Practices</h2>

			<h3>Server-Side Only</h3>

			<p>
				Always make API requests from your server, never from client-side code.
				This ensures your secret keys remain protected.
			</p>

			<CodeBlock language="typescript" title="Correct: Server-side API call (Node.js)">
				{`// In your backend server (Express, Fastify, etc.)
import express from 'express';

const app = express();

app.post('/api/create-session', async (req, res) => {
  const { playerId, currency } = req.body;

  // Secret key is only accessible on the server
  const response = await fetch('https://api.rngfort.com/api/v1/operators/sessions', {
    method: 'POST',
    headers: {
      'Authorization': \`Bearer \${process.env.RINGFORT_SECRET_KEY}\`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      playerId,
      currency,
      walletMode: 'integrated',
    }),
  });

  const data = await response.json();

  // Only return the game URL to the client, not the full response
  res.json({ gameUrl: data.data.gameUrl });
});`}
			</CodeBlock>

			<CodeBlock language="typescript" title="Incorrect: Client-side API call (never do this)">
				{`// NEVER DO THIS - Secret key would be exposed to users
const response = await fetch('https://api.rngfort.com/api/v1/operators/sessions', {
  method: 'POST',
  headers: {
    // This exposes your secret key in the browser!
    'Authorization': 'Bearer sk_live_your_secret_key',
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({ playerId: 'player_123' }),
});`}
			</CodeBlock>

			<h3>Environment Variables</h3>

			<p>
				Store your API keys in environment variables, not in your codebase.
			</p>

			<CodeBlock language="bash" title=".env file (not committed to git)">
				{`# Sandbox credentials
RINGFORT_PUBLIC_KEY=pk_test_abc123xyz789
RINGFORT_SECRET_KEY=sk_test_def456uvw012

# Live credentials (production only)
RINGFORT_PUBLIC_KEY_LIVE=pk_live_ghi789rst345
RINGFORT_SECRET_KEY_LIVE=sk_live_jkl012mno678`}
			</CodeBlock>

			<CodeBlock language="typescript" title="Using environment variables">
				{`// Access keys from environment variables
const secretKey = process.env.NODE_ENV === 'production'
  ? process.env.RINGFORT_SECRET_KEY_LIVE
  : process.env.RINGFORT_SECRET_KEY;

const response = await fetch('https://api.rngfort.com/api/v1/operators/sessions', {
  headers: {
    'Authorization': \`Bearer \${secretKey}\`,
    'Content-Type': 'application/json',
  },
  // ...
});`}
			</CodeBlock>

			<h3>Key Rotation</h3>

			<p>
				Regularly rotate your API keys as a security best practice. You can
				generate new keys from your operator dashboard without downtime by
				following these steps:
			</p>

			<ol>
				<li>Generate a new API key pair from your dashboard</li>
				<li>Update your server configuration with the new secret key</li>
				<li>Deploy the updated configuration</li>
				<li>Verify the new key works correctly</li>
				<li>Revoke the old key from your dashboard</li>
			</ol>

			<Alert variant="success" title="Zero-Downtime Rotation">
				Ringfort supports multiple active API keys per operator. This allows you
				to deploy a new key before revoking the old one, ensuring zero downtime
				during key rotation.
			</Alert>

			<h2>Webhook Signature Verification</h2>

			<p>
				When receiving wallet callbacks (seamless mode) or event webhooks,
				verify the signature to ensure the request came from Ringfort.
			</p>

			<CodeBlock language="typescript" title="Verify webhook signatures">
				{`import crypto from 'crypto';

function verifyWebhookSignature(
  payload: string,
  signature: string,
  webhookSecret: string
): boolean {
  const expectedSignature = crypto
    .createHmac('sha256', webhookSecret)
    .update(payload)
    .digest('hex');

  // Use timing-safe comparison to prevent timing attacks
  return crypto.timingSafeEqual(
    Buffer.from(signature),
    Buffer.from(expectedSignature)
  );
}

// In your webhook handler
app.post('/webhook/ringfort', express.raw({ type: 'application/json' }), (req, res) => {
  const signature = req.headers['x-signature'] as string;
  const payload = req.body.toString();

  if (!verifyWebhookSignature(payload, signature, process.env.RINGFORT_WEBHOOK_SECRET)) {
    return res.status(401).json({ error: 'Invalid signature' });
  }

  // Process the webhook
  const event = JSON.parse(payload);
  // ...

  res.json({ received: true });
});`}
			</CodeBlock>

			<h2>Error Responses</h2>

			<p>
				Authentication errors return a 401 status code with a descriptive error
				message:
			</p>

			<CodeBlock language="json" title="Missing authentication">
				{`{
  "success": false,
  "error": {
    "code": "UNAUTHORIZED",
    "message": "Missing or invalid Authorization header"
  }
}`}
			</CodeBlock>

			<CodeBlock language="json" title="Invalid API key">
				{`{
  "success": false,
  "error": {
    "code": "UNAUTHORIZED",
    "message": "Invalid API key"
  }
}`}
			</CodeBlock>

			<CodeBlock language="json" title="Expired session token">
				{`{
  "success": false,
  "error": {
    "code": "SESSION_EXPIRED",
    "message": "Game session has expired"
  }
}`}
			</CodeBlock>

			<div className={styles.nextSteps}>
				<h2>Next Steps</h2>

				<CardGrid columns={2}>
					<Card
						title="Environments"
						description="Learn about sandbox and live environments in detail."
						href="/docs/environments"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<circle cx="12" cy="12" r="10" />
								<line x1="2" y1="12" x2="22" y2="12" />
								<path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
							</svg>
						}
					/>

					<Card
						title="Sessions API"
						description="Create and manage player sessions."
						href="/docs/sessions"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
								<circle cx="12" cy="7" r="4" />
							</svg>
						}
					/>

					<Card
						title="Webhooks"
						description="Set up and verify webhook callbacks."
						href="/docs/webhooks"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
								<path d="M13.73 21a2 2 0 0 1-3.46 0" />
							</svg>
						}
					/>

					<Card
						title="Error Handling"
						description="Handle API errors gracefully."
						href="/docs/errors"
						icon={
							<svg
								width="20"
								height="20"
								viewBox="0 0 24 24"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
							>
								<circle cx="12" cy="12" r="10" />
								<line x1="12" y1="8" x2="12" y2="12" />
								<line x1="12" y1="16" x2="12.01" y2="16" />
							</svg>
						}
					/>
				</CardGrid>
			</div>
		</div>
	);
}
